import moment from 'moment';
import { Employee, IEmployee } from '../employee';
import { IUserData, User } from '../user';
import {
  IOvertimeApproval,
  IOvertimeApprovalData,
  IOvertimeApprovalFieldError,
  IOvertimeApprovalFieldErrorData,
  IOvertimeRequest,
  IOvertimeRequestAttachment,
  IOvertimeRequestAttachmentData,
  IOvertimeRequestAttachmentFieldError,
  IOvertimeRequestAttachmentFieldErrorData,
  IOvertimeRequestCommentFieldError,
  IOvertimeRequestCommentFieldErrorData,
  IOvertimeRequestData,
  IOvertimeRequestFieldError,
  IOvertimeRequestFieldErrorData,
  IOvertimeRequestStateInfoData,
  OvertimeApprovalState,
  OvertimeRequestStateEnum
} from './overtime-request.types';
import {
  AbstractMentionAttachmentComment,
  IMentionAttachmentComment,
  IMentionAttachmentCommentData
} from '../comment';
import {
  AbstractAttachment,
  IAttachment,
  IAttachmentData
} from '../attachment';

export class OvertimeRequest implements IOvertimeRequest {
  readonly id?: number;
  readonly state: OvertimeRequestStateEnum;
  readonly employee?: IEmployee; // this is optional employee is created in the backend and is not initialized to null
  readonly start: string | Date;
  readonly end: string | Date;
  readonly amount_requested?: number;
  readonly reason: string;
  readonly approvers: OvertimeApproval[];
  readonly attachments: IOvertimeRequestAttachment[] = [];
  readonly created_at: Date;
  readonly updated_at: Date;

  constructor(data: IOvertimeRequestData) {
    if (data.id) {
      this.id = data.id;
    }

    this.state = data.state ?? OvertimeRequestStateEnum.Draft;

    if (data.employee) {
      this.employee = new Employee(data.employee);
    }

    this.start = data.start;
    this.end = data.end;

    this.amount_requested = data.amount_requested ?? 0;

    this.reason = data.reason ?? '';

    this.approvers = data.approvers.map(
      approver => new OvertimeApproval(approver)
    );

    this.attachments = data.attachments.map(
      attachment => new OvertimeRequestAttachment(attachment)
    );

    this.created_at = moment(data.created_at as string).toDate();

    this.updated_at = moment(data.updated_at as string).toDate();
  }

  getStateInfo(): IOvertimeRequestStateInfoData {
    // Returns color based on state
    switch (this.state) {
      case OvertimeRequestStateEnum.Draft:
        return { display_name: 'Draft', color: 'draft' };

      case OvertimeRequestStateEnum.Submitted:
        return { display_name: 'Waiting for Approval', color: 'submitted' };

      case OvertimeRequestStateEnum.Approved:
        return { display_name: 'Approved', color: 'approved' };

      case OvertimeRequestStateEnum.Rejected:
        return { display_name: 'Rejected', color: 'reject' };

      case OvertimeRequestStateEnum.Cancelled:
        return { display_name: 'Cancelled', color: 'cancel' };

      default:
        return { display_name: '', color: 'white' };
    }
  }

  validate(): boolean {
    throw new Error('Method not implemented.');
  }

  // Manage attachments
  attachFile(file: File): void {
    this.attachments.push(new OvertimeRequestAttachment({ file: file }));
  }

  detachFile(file: string | File): void {
    const index = this.attachments.findIndex(
      attachment => attachment.file == file
    );

    if (index > -1) {
      this.attachments.splice(index, 1);
    }
  }
}

export class OvertimeApproval implements IOvertimeApproval {
  readonly id?: number | undefined;
  readonly approver: number | IUserData;
  readonly status: OvertimeApprovalState;
  readonly created_at?: string | undefined;
  readonly updated_at?: string | undefined;

  constructor(data: IOvertimeApprovalData) {
    if (data.id) {
      this.id = data.id;
    }

    if (typeof data.approver == 'number') {
      this.approver = data.approver;
    } else {
      this.approver = new User(data.approver);
    }

    this.status = data.status ?? OvertimeApprovalState.Tentative;

    if (data.created_at) {
      this.created_at = data.created_at;
    }

    if (data.updated_at) {
      this.updated_at = data.updated_at;
    }
  }

  validate(): boolean {
    throw new Error('Method not implemented.');
  }
}

export class OvertimeRequestAttachment implements IOvertimeRequestAttachment {
  readonly id?: number | undefined;
  readonly file: string | File;
  readonly filename: string = '';

  constructor(data: IOvertimeRequestAttachmentData) {
    if (data.id) {
      this.id = data.id;
    }

    if (data.filename) {
      this.filename = data.filename;
    }

    this.file = data.file;
  }

  validate(): boolean {
    throw new Error('Method not implemented.');
  }
}

export class OvertimeRequestCommentAttachment extends AbstractAttachment {
  constructor(data: IAttachmentData) {
    super(data);
  }

  toString(): string {
    return this.filename;
  }
}

export class OvertimeRequestComment extends AbstractMentionAttachmentComment
  implements IMentionAttachmentComment {
  readonly text: string = '';
  readonly attachments: IAttachment[] = [];

  constructor(data?: IMentionAttachmentCommentData) {
    super(data);

    if (data) {
      this.text = data.text ?? '';

      if (data.attachments) {
        this.attachments = data.attachments.map(
          attachment => new OvertimeRequestCommentAttachment(attachment)
        );
      }
    }
  }

  validate(): boolean {
    throw new Error('Method not implemented.');
  }

  toString() {
    return this.text;
  }
}

export class OvertimeRequestFieldError implements IOvertimeRequestFieldError {
  readonly start: string[] = [];
  readonly end: string[] = [];
  readonly amount_requested: string[] = [];
  readonly reason: string[] = [];
  readonly approvers: IOvertimeApprovalFieldErrorData[] = [];
  readonly attachments: IOvertimeRequestAttachmentFieldErrorData[] = [];
  readonly state: string[] = [];
  readonly employee: string[] = [];
  readonly owner: string[] = [];

  constructor(data?: IOvertimeRequestFieldErrorData) {
    if (data) {
      if (data.state) {
        this.state = data.state;
      }
      if (data.employee) {
        this.employee = data.employee;
      }
      if (data.owner) {
        this.owner = data.owner;
      }
      if (data.start) {
        this.start = data.start;
      }
      if (data.end) {
        this.end = data.end;
      }
      if (data.amount_requested) {
        this.amount_requested = data.amount_requested;
      }
      if (data.reason) {
        this.reason = data.reason;
      }

      if (data.approvers) {
        this.approvers = data.approvers.map(
          approver => new OvertimeApprovalFieldError(approver)
        );
      }

      if (data.attachments) {
        this.attachments = data.attachments.map(
          attachment => new OvertimeRequestAttachmentFieldError(attachment)
        );
      }
    }
  }

  clearErrors(): void {
    this.state.splice(0, this.state.length);
    this.employee.splice(0, this.employee.length);
    this.start.splice(0, this.start.length);
    this.end.splice(0, this.end.length);
    this.amount_requested.splice(0, this.amount_requested.length);
    this.reason.splice(0, this.reason.length);
    this.approvers.splice(0, this.approvers.length);
    this.attachments.splice(0, this.attachments.length);
  }

  static isOvertimeRequestFieldError(
    data: IOvertimeRequestFieldErrorData
  ): boolean {
    if (data.state && Array.isArray(data.state)) {
      return true;
    } else if (data.employee && Array.isArray(data.employee)) {
      return true;
    } else if (data.start && Array.isArray(data.start)) {
      return true;
    } else if (data.end && Array.isArray(data.end)) {
      return true;
    } else if (data.reason && Array.isArray(data.reason)) {
      return true;
    } else if (data.attachments && Array.isArray(data.attachments)) {
      return true;
    } else if (data.approvers && Array.isArray(data.approvers)) {
      return true;
    }
    return false;
  }
}

export class OvertimeApprovalFieldError implements IOvertimeApprovalFieldError {
  readonly approver: string[] = [];
  readonly status: string[] = [];

  constructor(data?: IOvertimeApprovalFieldErrorData) {
    if (data) {
      if (data.approver) {
        this.approver = data.approver;
      }
      if (data.status) {
        this.status = data.status;
      }
    }
  }

  clearErrors(): void {
    this.approver.splice(0, this.approver.length);
    this.status.splice(0, this.status.length);
  }

  static isOvertimeApprovalFieldError(
    data: IOvertimeApprovalFieldErrorData
  ): boolean {
    if (data.approver && Array.isArray(data.approver)) {
      return true;
    }
    if (data.status && Array.isArray(data.status)) {
      return true;
    }
    return false;
  }
}

export class OvertimeRequestAttachmentFieldError
  implements IOvertimeRequestAttachmentFieldError {
  readonly file: string[] = [];

  constructor(data?: IOvertimeRequestAttachmentFieldErrorData) {
    if (data) {
      if (data.file) {
        this.file = data.file;
      }
    }
  }

  clearErrors(): void {
    this.file.splice(0, this.file.length);
  }

  static isOvertimeRequestAttachmentFieldError(
    data: IOvertimeRequestAttachmentFieldErrorData
  ): boolean {
    if (data.file && Array.isArray(data.file)) {
      return true;
    }
    return false;
  }
}

export class OvertimeRequestCommentFieldError
  implements IOvertimeRequestCommentFieldError {
  readonly text: string[] = [];

  constructor(data: IOvertimeRequestCommentFieldErrorData) {
    if (data) {
      if (data.text) {
        this.text = data.text;
      }
    }
  }

  clearErrors(): void {
    this.text.splice(0, this.text.length);
  }
}
